import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Phone, Trash2, Clock, MapPinned, MapPin, WifiOff, CloudDownload, EyeOff, Eye, PartyPopper } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { saveTrip, loadTrip, isNetworkError, useOnline } from "@/lib/offline";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DirectionsMenu } from "@/components/DirectionsMenu";
import { CATEGORY_MAP, fmtDate } from "@/lib/categories";
import { api, errorMessage } from "@/lib/api";
import { to24, to12, startAt } from "@/lib/time";
import { TripToolbar } from "@/components/TripToolbar";
import { BudgetBar, dayBudget } from "@/components/TripBudget";

function TripItem({ entry, onMove, onRemove, onReveal, offline, myProfile }) {
  const l = entry.listing;
  const cat = CATEGORY_MAP[l.category] || CATEGORY_MAP.shows;
  const show = (l.schedule || []).find((s) => s.date === entry.day);
  return (
    <div data-testid={`trip-item-${entry.id}`} className="rounded-2xl bg-[#151C2C] border border-[#26334D] p-4 flex flex-wrap items-center gap-4">
      <span className="h-11 w-11 rounded-xl bg-[#FF3B6B]/15 text-[#FF3B6B] grid place-items-center shrink-0"><cat.icon className="h-5 w-5" /></span>
      <div className="flex-1 min-w-[180px]">
        <h3 className="font-semibold">{l.name}</h3>
        <p className="text-sm text-slate-400 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{entry.time ? `${show?.title ? show.title + " · " : ""}${entry.time}` : show ? `${show.title || "Show"} · ${show.time}` : l.hours || cat.label}</p>
        {l.address && <p data-testid={`trip-address-${entry.id}`} className="text-xs text-slate-300 mt-1 flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 shrink-0 text-[#00E5A3]" />{l.address}</p>}
        {l.phone && <a data-testid={`trip-phone-${entry.id}`} href={`tel:${l.phone.replace(/[^\d+]/g, "")}`} className="text-xs text-slate-300 mt-0.5 flex items-center gap-1 hover:text-white"><Phone className="h-3 w-3 text-[#00E5A3]" />{l.phone}</a>}
        <p className="text-xs text-slate-500 mt-0.5">{entry.city}{l.price_range ? <span className="ml-2 font-mono-vv text-[#FFB800]">{l.price_range}</span> : null}</p>
      </div>
      <input data-testid={`trip-move-${entry.id}`} disabled={offline} type="date" value={entry.day} onChange={(e) => onMove(entry, { day: e.target.value })}
        className="bg-[#0B0F19] border border-[#26334D] rounded-xl px-2 py-1.5 text-sm [color-scheme:dark]" />
      <input data-testid={`trip-time-${entry.id}`} disabled={offline} type="time" defaultValue={to24(entry.time)} onBlur={(e) => e.target.value !== to24(entry.time) && onMove(entry, { time: e.target.value })}
        className="bg-[#0B0F19] border border-[#26334D] rounded-xl px-2 py-1.5 text-sm w-28 [color-scheme:dark]" />
      <DirectionsMenu item={l} />
      {l.phone && (
        <Button asChild variant="outline" size="icon" className="rounded-full border-[#26334D] bg-transparent">
          <a data-testid={`trip-call-${entry.id}`} href={`tel:${l.phone.replace(/[^\d+]/g, "")}`}><Phone className="h-4 w-4" /></a>
        </Button>
      )}
      {myProfile && entry.added_by === myProfile && entry.hidden && entry.day === new Date().toLocaleDateString("en-CA") && (
        <Button data-testid={`trip-reveal-${entry.id}`} disabled={offline} size="sm" onClick={() => onReveal(entry)}
          className="rounded-full bg-[#FFB800] hover:bg-[#FFC933] text-[#0B0F19] font-semibold"><PartyPopper className="h-4 w-4 mr-1" /> Reveal surprise</Button>
      )}
      {myProfile && entry.added_by === myProfile && (
        <Button data-testid={`trip-surprise-${entry.id}`} disabled={offline} variant="outline" size="sm" onClick={() => onMove(entry, { hidden: !entry.hidden })}
          title="Hide this from your family so it stays a surprise"
          className={`rounded-full bg-transparent ${entry.hidden ? "border-[#FFB800] text-[#FFB800]" : "border-[#26334D] text-slate-300"}`}>
          {entry.hidden ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}{entry.hidden ? "Hidden from family" : "Hide from family"}
        </Button>
      )}
      <Button data-testid={`trip-remove-${entry.id}`} disabled={offline} variant="ghost" size="icon" onClick={() => onRemove(entry)}><Trash2 className="h-4 w-4 text-[#FF7A7A]" /></Button>
    </div>
  );
}

export default function Trip() {
  const { user } = useAuth();
  const online = useOnline();
  const [items, setItems] = useState(null);
  const [savedAt, setSavedAt] = useState(loadTrip(user.id)?.at || null);
  const [fromCache, setFromCache] = useState(false);
  const load = useCallback(() => api.get("/itinerary").then((r) => {
    setItems(r.data); setFromCache(false);
    saveTrip(user.id, r.data); setSavedAt(new Date().toISOString());
  }).catch((e) => {
    const cached = loadTrip(user.id);
    if (isNetworkError(e) && cached) { setItems(cached.items); setFromCache(true); }
    else toast.error(errorMessage(e));
  }), [user.id]);
  const offline = fromCache || !online;
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const g = {};
    (items || []).forEach((i) => { (g[i.day] = g[i.day] || []).push(i); });
    Object.values(g).forEach((list) => list.sort((a, b) => (to24(a.time) || "99").localeCompare(to24(b.time) || "99")));
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  const [party, setParty] = useState(Number(localStorage.getItem("vv_party")) || 2);
  const changeParty = (n) => { setParty(n); localStorage.setItem("vv_party", String(n)); };

  const move = async (entry, change) => {
    if ("hidden" in change) {
      try { await api.patch(`/itinerary/${entry.id}`, { hidden: change.hidden }); toast.success(change.hidden ? "Hidden from your family" : "Visible to your family"); load(); }
      catch (e) { toast.error(errorMessage(e)); }
      return;
    }
    const day = change.day ?? entry.day;
    const hhmm = change.time ?? to24(entry.time);
    try { await api.patch(`/itinerary/${entry.id}`, { day, time: to12(hhmm), start_at: startAt(day, hhmm) }); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const reveal = async (entry) => {
    try { await api.post(`/itinerary/${entry.id}/reveal`); toast.success("Surprise revealed to your family!"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const remove = async (entry) => {
    try { await api.delete(`/itinerary/${entry.id}`); toast.success("Removed from trip"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <p className="text-xs uppercase tracking-[0.2em] text-[#00E5A3]">Your plan</p>
      <h1 className="text-4xl font-extrabold tracking-tight mt-1">My Trip</h1>
      {offline ? (
        <p data-testid="trip-offline-notice" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FFB800]/15 text-[#FFB800] px-4 py-1.5 text-sm">
          <WifiOff className="h-4 w-4" /> Offline — showing your saved copy{savedAt ? ` from ${new Date(savedAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}. Calls still work; changes need a connection.
        </p>
      ) : savedAt && items?.length > 0 && (
        <p data-testid="trip-offline-ready" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#00E5A3]/10 text-[#00E5A3] px-4 py-1.5 text-sm">
          <CloudDownload className="h-4 w-4" /> Saved for offline · works with no signal
        </p>
      )}
      {!offline && <TripToolbar />}
      {items?.length > 0 && <BudgetBar items={items} party={party} setParty={changeParty} />}
      {items && items.length === 0 && (
        <div data-testid="trip-empty" className="mt-10 rounded-2xl border border-dashed border-[#26334D] p-12 text-center text-slate-400">
          <MapPinned className="h-8 w-8 mx-auto mb-3 text-slate-500" />
          Nothing planned yet. Open any listing and tap <span className="text-[#00E5A3] font-semibold">Add to my trip</span>.
          <div className="mt-5"><Link to="/explore" data-testid="trip-explore-link" className="px-5 py-2.5 rounded-full bg-[#FF3B6B] text-white font-semibold">Find things to do</Link></div>
        </div>
      )}
      <div className="mt-10 space-y-10">
        {days.map(([day, list], i) => (
          <section key={day} data-testid={`trip-day-${day}`} className="rise" style={{ animationDelay: `${i * 60}ms` }}>
            <h2 className="flex items-center gap-2 text-lg font-bold mb-4">
              <span className="font-mono-vv text-xs text-[#FF3B6B]">DAY {i + 1}</span>
              <CalendarDays className="h-4 w-4 text-slate-500" /> {fmtDate(day)}
              <span className="text-sm text-slate-500 font-normal">· {list.length} {list.length === 1 ? "stop" : "stops"}</span>
              <span data-testid={`trip-day-budget-${day}`} className="ml-auto text-sm font-semibold text-[#FFB800]">≈ ${dayBudget(list, party).total.toLocaleString()}</span>
            </h2>
            <div className="space-y-3">{list.map((e) => <TripItem key={e.id} entry={e} onMove={move} onRemove={remove} onReveal={reveal} offline={offline} myProfile={user.family_plan ? user.active_profile?.id : null} />)}</div>
          </section>
        ))}
      </div>
    </main>
  );
}
